import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DonneesMatiereReleve {
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  credits: number;
  note_d1: number | null;
  note_d2: number | null;
  absent_d1: boolean;
  absent_d2: boolean;
  moy_devoirs: number | null;
  note_examen: number | null;
  absent_examen: boolean;
  /** true si la note d'examen utilisée provient du rattrapage */
  est_rattrapage: boolean;
  moyenne_ecue: number | null;
  validee: boolean;
}

export interface DonneesUEReleve {
  ue_id: number;
  code_ue: string;
  nom_ue: string;
  validee: boolean;
  /** UE validée par compensation (moy < 10 mais validée parce que moy_sem ≥ 10) */
  validee_par_compensation?: boolean;
  /** UE compensable (8 ≤ moy < 10) — peut être validée si moy_sem ≥ 10 et pas d'éliminatoire */
  compensable?: boolean;
  /** UE éliminatoire (moy < 6) — bloque la validation du semestre */
  eliminatoire?: boolean;
  moyenne_ue: number | null;
  credits_obtenus: number;
  credits_total: number;
  /** 'normale' = validé en 1ère session, 'rattrapage' = validé après rattrapage, '' sinon */
  session_validation: '' | 'normale' | 'rattrapage';
  matieres: DonneesMatiereReleve[];
}

export interface DonneesReleve {
  etudiant: {
    id: number;
    matricule: string;
    nom: string;
    prenoms: string;
    date_naissance: string | null;
    lieu_naissance: string;
    niveau: string;
    option: string;
    classe: string;
    cycle: string;
    photo_url: string | null;
    type_formation?: string;
  };
  annee_academique: string;
  semestre: number;
  ues: DonneesUEReleve[];
  resultat_semestre: {
    moyenne_semestre: number | null;
    credits_obtenus: number;
    credits_requis: number;
    valide: boolean;
    rang: number | null;
    effectif_classe: number | null;
    /** 'normale' = validé en 1ère session, 'rattrapage' = validé après rattrapage, '' sinon */
    session_validation: '' | 'normale' | 'rattrapage';
  } | null;
  /** Cursus Double Diplôme (présent uniquement pour un étudiant DD au semestre pair). */
  double_diplome?: {
    modules: { module: string; formateur: string; provenance: string; piste: string; note: number | null }[];
    moyenne_fi: number | null;
    moyenne_dd: number | null;
    moyenne_generale: number | null;
    mention_generale: string;
  } | null;
}

export interface DonneesClasseReleve {
  classe_code: string;
  semestre: number;
  annee_academique: string;
  nombre_etudiants: number;
  etudiants: DonneesReleve[];
  /** Fix 2026-09-07 : étudiants exclus car ayant des dettes (agent) */
  etudiants_exclus_dette?: Array<{
    matricule: string;
    nom: string;
    prenoms: string;
    solde_total: string;
    mode: string;
  }>;
  nombre_exclus_dette?: number;
}

export interface ImpressionCreate {
  matricule?: string;
  classe_code?: string;
  type_releve: 'provisoire' | 'definitif';
  scope: 'individuel' | 'classe' | 'filtre';
  semestre: number;
  annee_academique: string;
  nombre_copies?: number;
  numero_reference_externe?: string;
  qr_code_data?: string;
}

export interface ImpressionReleve {
  id: number;
  etudiant: number | null;
  etudiant_nom: string | null;
  classe_code: string;
  type_releve: string;
  type_releve_display: string;
  scope: string;
  scope_display: string;
  semestre: number;
  annee_academique: string;
  nombre_copies: number;
  numero_reference_externe: string;
  imprime_par: number | null;
  imprime_par_nom: string | null;
  date_impression: string;
}

export interface HistoriqueResponse {
  total: number;
  ce_mois: number;
  provisoires: number;
  definitifs: number;
  impressions: ImpressionReleve[];
}

// Accès léger scolarité (2026-09-12) : joint au refus 403 « dette_paiement »
export interface ReleveLegerAgent {
  semestre: number;
  annee_academique: string;
  etudiant: { matricule: string; nom: string; prenoms: string; classe: string; niveau: string; option: string };
  periodes_dues: { periode: string; label: string; present: boolean; solde: string | null }[];
  decision_disponible: boolean;
  message?: string;
  valide?: boolean;
  ues_a_rattraper?: {
    code_ue: string;
    nom_ue: string;
    prioritaire: boolean;
    matieres: { code_matiere: string; nom_matiere: string }[];
  }[];
}

export interface DonneesReleveParams {
  matricule: string;
  semestre: number;
  annee_academique: string;
}

export interface DonneesClasseParams {
  classe_code: string;
  semestre: number;
  annee_academique: string;
  moy_min?: string;
  moy_max?: string;
}

export interface HistoriqueParams {
  annee_academique?: string;
  type_releve?: string;
  date_debut?: string;
  date_fin?: string;
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const relevesApi = createApi({
  reducerPath: 'relevesApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['ImpressionReleve'],
  endpoints: (builder) => ({
    // Données d'un étudiant pour le relevé
    // NB : on FORCE le header X-Annee-Academique à la valeur choisie dans le
    // select, sinon le middleware backend reste sur l'année globale du
    // localStorage et lit le mauvais schéma → aucune donnée trouvée quand
    // l'utilisateur consulte une année archivée.
    getDonneesReleve: builder.query<DonneesReleve, DonneesReleveParams>({
      query: ({ matricule, semestre, annee_academique }) => ({
        url: `/releves/donnees-releve/?matricule=${encodeURIComponent(matricule)}&semestre=${semestre}&annee_academique=${encodeURIComponent(annee_academique)}`,
        headers: { 'X-Annee-Academique': annee_academique },
      }),
    }),

    // Données de toute une classe pour le relevé — même override du header
    getDonneesClasse: builder.query<DonneesClasseReleve, DonneesClasseParams>({
      query: ({ classe_code, semestre, annee_academique, moy_min, moy_max }) => {
        const params = new URLSearchParams({
          classe_code,
          semestre: String(semestre),
          annee_academique,
        });
        if (moy_min) params.set('moy_min', moy_min);
        if (moy_max) params.set('moy_max', moy_max);
        return {
          url: `/releves/donnees-classe/?${params.toString()}`,
          headers: { 'X-Annee-Academique': annee_academique },
        };
      },
    }),

    // Enregistrer une impression
    enregistrerImpression: builder.mutation<ImpressionReleve, ImpressionCreate>({
      query: (body) => ({
        url: '/releves/enregistrer-impression/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ImpressionReleve'],
    }),

    // Historique des impressions avec stats
    getHistoriqueImpressions: builder.query<HistoriqueResponse, HistoriqueParams>({
      query: (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.annee_academique) params.set('annee_academique', filters.annee_academique);
        if (filters.type_releve) params.set('type_releve', filters.type_releve);
        if (filters.date_debut) params.set('date_debut', filters.date_debut);
        if (filters.date_fin) params.set('date_fin', filters.date_fin);
        const qs = params.toString();
        return `/releves/historique-impressions/${qs ? '?' + qs : ''}`;
      },
      providesTags: ['ImpressionReleve'],
    }),

    // QR + n° de référence déjà émis pour un relevé définitif d'un étudiant
    // (réutilisés à la réimpression). 2026-10-14.
    getQrDefinitifExistant: builder.query<
      { existe: boolean; qr_code_data?: string; numero_reference_externe?: string; date_impression?: string | null },
      { matricule: string; semestre: number; annee_academique: string }
    >({
      query: ({ matricule, semestre, annee_academique }) => ({
        url: `/releves/qr-definitif-existant/?matricule=${encodeURIComponent(matricule)}&semestre=${semestre}&annee_academique=${encodeURIComponent(annee_academique)}`,
        headers: { 'X-Annee-Academique': annee_academique },
      }),
      providesTags: ['ImpressionReleve'],
    }),
  }),
});

export const {
  useGetDonneesReleveQuery,
  useLazyGetDonneesReleveQuery,
  useGetDonneesClasseQuery,
  useLazyGetDonneesClasseQuery,
  useEnregistrerImpressionMutation,
  useGetHistoriqueImpressionsQuery,
  useLazyGetQrDefinitifExistantQuery,
} = relevesApi;
